import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Form, Spinner, Offcanvas, Button, Table } from 'react-bootstrap'
import { downloadAgingReportPdf, getAgingReportAsync, GetAgingStatusesAsync } from '../../../services/agingService/agingService'
import { AgingReportDto, AgingReportFilterReqDto, AgingStatusDto, SelectOption } from '../../../interfaces/agingReport/agingReport'
import { useAuth } from '../../../firebase/AuthProvider'
import { toast } from 'react-toastify'
import Pagination from '../../ui/Pagination/Pagination'
import { PaginationDto } from '../../../interfaces/Employee/Employee'
import EditAgingModal from './modals/EditAgingModal'
import filtericon from '../../../assets/images/filtericon.svg';
import searchicon from '../../../assets/images/searchicon.svg';
import AgingReportFilter from './AgingReportFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import Breadcrumbs from '../../navigation/Breadcrumb/Breadcrumb'
import './AgingReport.css'
import NoParticipantsFound from '../participantfiles/noParticipantsFound'

const AgingReport = () => {
    const { currentUser } = useAuth();
    const location = useLocation();
    // data 
    const [agingReport, setAginReport] = useState<AgingReportDto[]>([]);
    const [selectedStatusOption, setSelectedStatusOption] = useState<SelectOption | null>(null);
    const [selectedWaiverOptions, setSelectedWaiverOptions] = useState<SelectOption[]>([]);
    const [selectedRegionOptions, setSelectedRegionOptions] = useState<SelectOption[]>([]); // <-- Add this
    const [statuses, setStatuses] = useState<AgingStatusDto[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDownloadLoading, setDownloadLoading] = useState<boolean>(false);

    //Modal state
    const [showEditAgingModal, setShowEditAgingModal] = useState<boolean>(false);
    const [selectedAging, setSelectedAging] = useState<AgingReportDto>();

    // Pagination
    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: 1, pageSize: 10 });
    const [agingReportCount, setAginReportCount] = useState<number>();
    const [totalCount , setTotalCount] = useState<number>(0);

    // auto reload
    const [reload, setReload] = useState<boolean>(true);
    const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Get userId from URL if present (from Hometabs "View all" link)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const userIdFromUrl = params.get("userId");
        // Store userId in state or use it directly in getAgingReport
        // For now, we'll use it directly in getAgingReport
    }, [location.search]);

    useEffect(() => {
        getAgingStatus();
    }, []);


    useEffect(() => {
        getAgingReport();
    }, [pagination, reload, selectedStatusOption, selectedWaiverOptions, selectedRegionOptions, location.search]); // Add location.search

    const getAgingReport = async () => {
        if (!currentUser) return;
        
        // Get userId and minAgingDays from URL if present (from Hometabs "View all" link)
        const params = new URLSearchParams(location.search);
        const userIdFromUrl = params.get("userId");
        const minAgingDaysFromUrl = params.get("minAgingDays");
        const targetUserId = userIdFromUrl || currentUser.uid;
        
        // Parse minAgingDays from URL (convert string to number or null)
        const minAgingDays = minAgingDaysFromUrl ? parseInt(minAgingDaysFromUrl, 10) : null;
        
        const payload: AgingReportFilterReqDto = {
            regionIds: selectedRegionOptions.length > 0
                ? selectedRegionOptions.map(opt => opt.value)
                : null,
            waiverIds: selectedWaiverOptions.length > 0
                ? selectedWaiverOptions.map(opt => opt.value)
                : null,
            statusId: selectedStatusOption?.value || null,
            pageNumber: pagination.pageNumber,
            pageSize: pagination.pageSize
        }
        try {
            const token = await currentUser.getIdToken();
            setIsLoading(true);
            setAginReport([]);
            const res = await getAgingReportAsync(payload, targetUserId, token, minAgingDays);
            if (res.success) {
                setAginReport(res.data.items);
                setAginReportCount(res.data.totalCount);
                setTotalCount(res?.data?.totalCount || 0);
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            toast.error(error?.message);
        } finally { setIsLoading(false) }
    }

    const getAgingStatus = async () => {
        if (!currentUser) return;
        const token = await currentUser.getIdToken();
        const res = await GetAgingStatusesAsync(token);
        if (res.success) {
            setStatuses(res.data);

        } else {
            toast.error(res.message);
        }
    };

    const handleEditComment = (aging: AgingReportDto) => {
        setSelectedAging(aging);
        handlShowEditAgingModal();
    }

    const onPageChange = (pageNum: number) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: pageNum
        }))
    }

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPagination({
            pageNumber: pageNumber,
            pageSize: pageSize
        })
    }

    const handleCloseEditAgingModal = () => {
        setShowEditAgingModal(false);
    }

    const getEclipsedComment = (comment : string | null ) => {
        if(!comment || comment === "" || comment === null) return "";
        
        const updateComment = comment?.length <  50 ? comment : `${comment?.substring(0,50)}......`;
        
        return updateComment || "";
    }

    const handlShowEditAgingModal = () => setShowEditAgingModal(true);

    async function handleDownloadPdf() {
        try {
            const token = await currentUser?.getIdToken();
            if (!token) return;
            const payload: AgingReportFilterReqDto = {
                regionIds: selectedRegionOptions.length > 0
                    ? selectedRegionOptions.map(opt => opt.value)
                    : null,
                waiverIds: selectedWaiverOptions.length > 0
                    ? selectedWaiverOptions.map(opt => opt.value)
                    : null,
                statusId: selectedStatusOption?.value || null,
                pageNumber: 1,
                pageSize: 10000 // Large page size to get all filtered results
            }
            setDownloadLoading(true);
            const blob = await downloadAgingReportPdf(payload, token);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "ParticipantAgingReport.pdf";
            link.click();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("PDF download failed:", error);
            toast.error("Failed to download report");
        } finally { setDownloadLoading(false) }
    }


    // Filter aging report based on search term
    const filteredAgingReport = agingReport.filter((ag) => {
        if (!searchTerm.trim()) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            ag.firstName?.toLowerCase().includes(searchLower) ||
            ag.lastName?.toLowerCase().includes(searchLower) ||
            ag.waiver?.toLowerCase().includes(searchLower) ||
            ag.currentStatus?.toLowerCase().includes(searchLower) ||
            ag.latestComment?.toLowerCase().includes(searchLower)
        );
    });

    return (
        <>
            <Breadcrumbs />
            <div className='common_card'>
                <div className="aging-report-header">
                    <div className="top top_bar_flx">
                    <div className="top_bar_tittle">
                        <Form.Group
                            controlId="search"
                            className="custom_input_flled filled_input_icon mb-0"
                        >
                            <Form.Control
                                type="text"
                                value={searchTerm}
                                placeholder="Search Report"
                                className="search_input"
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <span className="icon_img cursor-pointer">
                                <img src={searchicon} alt="search" />
                            </span>
                            {filteredAgingReport.length > 0 && (
                            <span className="ml-3 aging-report-count-display">
                                {totalCount || "0"}
                            </span>
                        )}
                        </Form.Group>
                     
                    </div>
                    <div className="top_right_flx sign_documents_top_right_flx">
                        <Button style={{ minWidth: 'unset' }} variant="light" className="btn_sm rounded-3" onClick={() => setShowFilterOffcanvas(true)}><svg width="67" height="17" viewBox="0 0 67 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19.2487 3.66675H12.832" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M9.16667 3.66675H2.75" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M19.25 11H11" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M7.33333 11H2.75" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M19.2513 18.3333H14.668" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M11 18.3333H2.75" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M12.832 1.83325V5.49992" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M7.33203 9.16675V12.8334" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M14.668 16.5V20.1667" stroke="#3F3F46" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M39.1562 7.98438H34.7812V11.8594H38.8281V13.0391H34.7812V18H33.4688V6.79688H39.1562V7.98438ZM41.7656 7.96875C41.5365 7.96875 41.3411 7.89062 41.1797 7.73438C41.0182 7.57812 40.9375 7.38021 40.9375 7.14062C40.9375 6.90104 41.0182 6.70312 41.1797 6.54688C41.3411 6.38542 41.5365 6.30469 41.7656 6.30469C42 6.30469 42.1979 6.38542 42.3594 6.54688C42.526 6.70312 42.6094 6.90104 42.6094 7.14062C42.6094 7.36979 42.526 7.5651 42.3594 7.72656C42.1979 7.88802 42 7.96875 41.7656 7.96875ZM42.3906 18H41.1094V10H42.3906V18ZM46.2656 18H44.9844V6.15625H46.2656V18ZM52.5703 17.9219C52.2682 18.0885 51.8698 18.1719 51.375 18.1719C49.974 18.1719 49.2734 17.3906 49.2734 15.8281V11.0938H47.8984V10H49.2734V8.04688L50.5547 7.63281V10H52.5703V11.0938H50.5547V15.6016C50.5547 16.138 50.6458 16.5208 50.8281 16.75C51.0104 16.9792 51.3125 17.0938 51.7344 17.0938C52.0573 17.0938 52.3359 17.0052 52.5703 16.8281V17.9219ZM60.7109 14.3203H55.0625C55.0833 15.2109 55.3229 15.8984 55.7812 16.3828C56.2396 16.8672 56.8698 17.1094 57.6719 17.1094C58.5729 17.1094 59.401 16.8125 60.1562 16.2188V17.4219C59.4531 17.9323 58.5234 18.1875 57.3672 18.1875C56.237 18.1875 55.349 17.8255 54.7031 17.1016C54.0573 16.3724 53.7344 15.349 53.7344 14.0312C53.7344 12.7865 54.0859 11.7734 54.7891 10.9922C55.4974 10.2057 56.375 9.8125 57.4219 9.8125C58.4688 9.8125 59.2786 10.151 59.8516 10.8281C60.4245 11.5052 60.7109 12.4453 60.7109 13.6484V14.3203ZM59.3984 13.2344C59.3932 12.4948 59.2135 11.9193 58.8594 11.5078C58.5104 11.0964 58.0234 10.8906 57.3984 10.8906C56.7943 10.8906 56.2812 11.1068 55.8594 11.5391C55.4375 11.9714 55.1771 12.5365 55.0781 13.2344H59.3984ZM66.8281 11.2969C66.6042 11.125 66.2812 11.0391 65.8594 11.0391C65.3125 11.0391 64.8542 11.2969 64.4844 11.8125C64.1198 12.3281 63.9375 13.0312 63.9375 13.9219V18H62.6562V10H63.9375V11.6484H63.9688C64.151 11.0859 64.4297 10.6484 64.8047 10.3359C65.1797 10.0182 65.599 9.85938 66.0625 9.85938C66.3958 9.85938 66.651 9.89583 66.8281 9.96875V11.2969Z" fill="#71717A" />
                        </svg>
                        </Button>
                        <Button size='sm' className=" btn_sm rounded-3" onClick={handleDownloadPdf} disabled={isDownloadLoading}>
                            {isDownloadLoading
                                ? <span><Spinner size='sm' /> Downloading...</span>
                                : <span className='download-report-text'> Download Report </span>}
                        </Button>
                    </div>
                </div>
                <hr className="aging-report-divider" />
                </div>

                <div className="overflow-x-auto w-full mt-2">
                    {
                        isLoading ? (
                            <div className="flex items-center justify-center py-5">
                                <p className='text-xl'>
                                    <Spinner size='sm' /> Loading...
                                </p>
                            </div>
                        ) : (
                            <>
                                {
                                    filteredAgingReport.length === 0 ? (
                                        <div className="flex items-center justify-center py-5">
                                            <div className="">
                                                <NoParticipantsFound/>
                                            </div>
                                        </div>
                                    ) : (
                                        <Table className="common_table min-w-[1100px]" style={{
                                            tableLayout: 'fixed',
                                            border: '#ffff',
                                        }}>
                                            <thead>
                                                <tr>
                                                </tr>
                                            </thead>
                                            <tbody
                                                className={`flex flex-col h-full overflow-y-hidden ${pagination.pageSize === 10
                                                    ? 'page-size-10'
                                                    : ''
                                                    }`}
                                            >
                                                {
                                                    filteredAgingReport.map((ag) => {
                                                        return (
                                                            <tr
                                                                key={ag.agingId}
                                                                className="flex justify-between w-full my-1 rounded-lg row-height-10 aging-report-card"
                                                                style={{
                                                                    alignItems: "center",
                                                                    borderTop: "1px solid #C9C9C9",
                                                                    borderRight: "1px solid #C9C9C9",
                                                                    borderBottom: "1px solid #C9C9C9",
                                                                    borderLeft: "1px solid #C9C9C9",
                                                                    padding: "0rem 1.25rem",
                                                                    backgroundColor: "#ffffff",
                                                                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                                                }}
                                                            >
                                                                <td className="w-[50%] py-2 gap-1 px-3 aging-report-card-left">
                                                                    <div className="aging-report-card-header">
                                                                        <span className="aging-report-waiver">{ag.waiver}</span>
                                                                        <span className="aging-report-name">{ag.firstName} {ag.lastName}</span>
                                                                    </div>
                                                                    <div className="  aging-report-comment cursor-pointer" onClick={() => handleEditComment(ag)}>
                                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="aging-report-comment-icon">
                                                                            <path d="M12.668 8.66675C12.668 9.02037 12.5275 9.35951 12.2774 9.60956C12.0274 9.85961 11.6883 10.0001 11.3346 10.0001H3.33464L0.667969 12.6667V2.00008C0.667969 1.64646 0.808444 1.30732 1.05849 1.05727C1.30854 0.807224 1.64768 0.666748 2.0013 0.666748H11.3346C11.6883 0.666748 12.0274 0.807224 12.2774 1.05727C12.5275 1.30732 12.668 1.64646 12.668 2.00008V8.66675Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                        <span className="aging-report-comment-text">{getEclipsedComment(ag?.latestComment) || "Add comment..."}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="w-[50%] py-2 px-3 aging-report-card-right">
                                                                    <span className="aging-report-date">Linked : {moment.utc(ag.linkageDate).local().format("MMM DD, YYYY")}</span>
                                                                    <span className="aging-report-badge aging-report-badge-aging">
                                                                        Aging - {Math.floor(ag.agingDays)}
                                                                    </span>
                                                                    <span className="aging-report-badge aging-report-badge-status">
                                                                        {ag.currentStatus || '--'}
                                                                    </span>

                                                                    <div className="aging-report-comment cursor-pointer" onClick={() => handleEditComment(ag)}>
                                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M10 2.5H4.16667C3.72464 2.5 3.30072 2.67559 2.98816 2.98816C2.67559 3.30072 2.5 3.72464 2.5 4.16667V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H15.8333C16.2754 17.5 16.6993 17.3244 17.0118 17.0118C17.3244 16.6993 17.5 16.2754 17.5 15.8333V10" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                                            <path d="M15.3132 2.18744C15.6447 1.85592 16.0944 1.66968 16.5632 1.66968C17.0321 1.66968 17.4817 1.85592 17.8132 2.18744C18.1447 2.51897 18.331 2.9686 18.331 3.43744C18.331 3.90629 18.1447 4.35592 17.8132 4.68744L10.3024 12.1991C10.1045 12.3968 9.86007 12.5415 9.59156 12.6199L7.1974 13.3199C7.12569 13.3409 7.04968 13.3421 6.97732 13.3236C6.90496 13.305 6.83892 13.2674 6.7861 13.2146C6.73328 13.1618 6.69564 13.0957 6.6771 13.0234C6.65856 12.951 6.65981 12.875 6.68073 12.8033L7.38073 10.4091C7.4595 10.1408 7.60451 9.89666 7.8024 9.69911L15.3132 2.18744Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })
                                                }
                                            </tbody>
                                        </Table>
                                    )
                                }
                            </>
                        )
                    }
                </div>

                {(agingReportCount && agingReportCount > 0) ? (
                    <div className="aging-report-pagination">
                        <Pagination
                            currentPage={pagination.pageNumber}
                            totalPages={Math.ceil(agingReportCount / pagination.pageSize)}
                            onPageChange={onPageChange}
                            pageSize={pagination.pageSize}
                            resetPageSize={resetPageSize}
                        />
                    </div>
                ) : <></>}


            {
                // Edit aging Modal (status / comment)
                showEditAgingModal && selectedAging &&
                (
                    <EditAgingModal
                        showEditAgingModal={showEditAgingModal}
                        handlCloseEditAgingModal={handleCloseEditAgingModal}
                        selectedAging={selectedAging}
                        reload={() => setReload(!reload)}
                        statuses={statuses} // Pass empty or fetch in modal if needed
                    />
                )
            }

            <AgingReportFilter
                show={showFilterOffcanvas}
                onHide={() => setShowFilterOffcanvas(false)}
                selectedStatusOption={selectedStatusOption}
                setSelectedStatusOption={setSelectedStatusOption}
                selectedWaiverOptions={selectedWaiverOptions}
                setSelectedWaiverOptions={setSelectedWaiverOptions}
                selectedRegionOptions={selectedRegionOptions} // <-- Add this
                setSelectedRegionOptions={setSelectedRegionOptions} // <-- Add this
                onApply={() => setReload(!reload)}
                statuses={statuses}
            />
            </div>
        </>
    )
}

export default AgingReport